import { Client } from "@/Common/Api/Client";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientPortalProfileManagementModel } from "@/Common/Models/Patient/UI/Profile/IPatientPortalProfileManagementModel";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { FeatureFlagControl } from "../FeatureFlagControl";
import { Popup } from "../Modals/Popup";
import { withPortalCommunicationMessageForm, WithPortalCommunicationMessageFormProps } from "../SharedItem/hoc/withPortalCommunicationMessageForm";
import { usePortalCommunicationMessageForm } from "../SharedItem/Hooks/usePortalCommunicationMessageForm";
import { PortalCommunicationMessageForm } from "../SharedItem/PortalCommunicationMessageForm";
import { AccountDetails } from "./Management/AccountDetails";
import { AccountUserLinked } from "./Management/AccountUserLinked";
import { AccountUserSharing } from "./Management/AccountUserSharing";
import { PatientMessaging } from "./Management/PatientMessaging";
import { SubscriptionDetails } from "./Management/SubscriptionDetails";
import { SubscriptionUserManagement } from "./Management/SubscriptionUserManagement";
import { PortalRegisterControl } from "./PortalRegistration";

import "./PortalStatus.css";

interface PortalStatusProps {
    patientInfo: Patient;
    patientId: string;
    onCloseCallback?: () => void;
    individualInfo: Individual;
    profileModel: IPatientPortalProfileManagementModel;
}

class PortalStatusState {
    public isLoading: boolean;
    public canSendMessageToPortal: boolean;
    public isClickableTextOpen: boolean;
    public portalMessageText: string;
    public isSendingMessage: boolean;
    public wasOriginallyChildAccount: boolean;
    public sendToPortalErrorMessage: string[];
    public subjectText: string;
    public showMessageClickableText: boolean;
}

type CombinedPortalStatusProps = PortalStatusProps &
ReturnType<typeof usePortalCommunicationMessageForm> &
WithPortalCommunicationMessageFormProps;

class PortalStatus extends BaseReactComponent<CombinedPortalStatusProps, PortalStatusState> {
    constructor(props: CombinedPortalStatusProps) {
        super(props);

        const state = new PortalStatusState();
        state.isLoading = true;
        state.isSendingMessage = false;
        state.wasOriginallyChildAccount = false;
        state.sendToPortalErrorMessage = [];
        if (this.props.patientInfo.IndividualResponse)
            state.wasOriginallyChildAccount = this.props.patientInfo.IndividualResponse.PortalSyncState === PortalRegistrationState.ChildAccount;
        state.showMessageClickableText = false;
        this.state = state;
    }

    private readonly isBidirectionalCommunicationEnabled: boolean = cacheStore.ClinicSettings.EnableBidirectionalCommunication;

    public componentDidMount(): void {
        super.setOverlay("PortalStatusPopup", {
            transparent: false
        });

        window.setTimeout(() => {
            sessionStore.dispatch(LocalOperations.Portal.PortalManagment_LoadPortalProfileOverview, {
                patientId: this.props.patientId
            });
        }, 10);

        $("#portalMessageTextBox").focus();
    }

    public componentWillReceiveProps(nextProps: Readonly<PortalStatusProps>): void {
        if (nextProps.profileModel)
            this.mutateState(s => s.isLoading = false);
    }

    private renderFooter(isChildAccount: boolean, hasSendMessageToPortalError: boolean): JSX.Element {
        return (
            <div className="row p-footer">
                <div className="pull-right">
                    <button
                        onClick={() => this.onCloseClick()}
                        className="btn btn-default push-right-5"
                    >
                        <span>{cacheStore.GetString("CloseLabel")}</span>
                    </button>
                    {(!this.state.wasOriginallyChildAccount || isChildAccount) && (
                        <button
                            onClick={() => this.isBidirectionalCommunicationEnabled ?
                                this.onSendPortalCommunication() :
                                this.onSendMessageClick()
                            }
                            disabled={this.disableSendToPortalButon(hasSendMessageToPortalError)}
                            className="btn btn-primary push-right-5"
                        >
                            <span>{cacheStore.GetString("SendToPatientPortal")}</span>
                        </button>
                    )}
                </div>
            </div>
        );
    }

    private closeClickableTextPanel(): void {
        this.mutateState(s => {
            s.showMessageClickableText = false;
        });
    }

    private openMessageClickableTextPanel(): void {
        this.mutateState(s => {
            s.showMessageClickableText = true;
        });
    }

    public render(): JSX.Element {
        let portalPatientOverview: GetPatientOverviewResponse = this.props.profileModel.get("PatientProfileOverview");
        let isSubscriptionEnabled = cacheStore.portalConfiguration?.HasSubscriptions;
        let hasSendMessageToPortalError = this.state.sendToPortalErrorMessage?.length > 0;
        let isPendingAccount = false;
        let isChildAccount = false;

        if (this.props.patientInfo.IndividualResponse) {
            let portalSyncState = this.props.patientInfo.IndividualResponse.PortalSyncState;
            isPendingAccount = portalSyncState === PortalRegistrationState.RegistrationPending;
            isChildAccount = portalSyncState === PortalRegistrationState.ChildAccount;
        }

        let showRegistrationConfirmation = this.state.wasOriginallyChildAccount && !isChildAccount;
        let removeScroll: React.CSSProperties = {};
        if (showRegistrationConfirmation) {
            removeScroll.overflowY = "auto";
        }

        return (
            <div onClick={() => this.closeClickableTextPanel()}>
                <Popup
                    width={700}
                    title={cacheStore.GetString("PortalAccountStatusLabel")}
                    Id="portal-status-popup"
                    dataCy="portal-status-popup"
                    onCloseDelegate={() => this.onCloseClick()}
                    cssClass="request-popup container"
                    customFooter={this.renderFooter(isChildAccount, hasSendMessageToPortalError)}
                    topPosition={50}
                    draggable={cacheStore.ClinicSettings.EnableBidirectionalCommunication}
                >
                    <div className="p-body container-fluid portal-status-body">
                        {this.state.isLoading && (
                            <InformationLoadingComponent
                                smallVersion={true}
                                customMessage={cacheStore.GetString("WaitForPortalLabel")}
                            />
                        )}

                        {portalPatientOverview != null && !isChildAccount && (
                            <div className="portal-status-popup-patient-details">
                                <AccountDetails
                                    patientInfo={this.props.patientInfo}
                                    overview={portalPatientOverview}
                                    patientContactId={this.props.patientId}
                                    areControlsDisabled={true}
                                    isStatusPopup
                                />
                                {this.isBidirectionalCommunicationEnabled && (
                                    <PatientMessaging
                                        patientContactId={this.props.patientId}
                                    />
                                )}
                            </div>
                        )}

                        {(isChildAccount || this.state.wasOriginallyChildAccount) && (
                            <PortalRegisterControl
                                patientInfo={this.props.patientInfo}
                                showRegistrationConfirmation={showRegistrationConfirmation}
                                showAsPopup={false}
                            />
                        )}

                        {PortalHelper.hasAccessToOtherProfiles(portalPatientOverview, this.props.patientId) && (
                            <AccountUserSharing
                                portalUser={portalPatientOverview.Account}
                                patientContactId={this.props.patientId}
                                areControlsDisabled={true}
                            />
                        )}

                        {PortalHelper.hasGrantedAccessToOtherProfiles(portalPatientOverview) && (
                            <AccountUserLinked
                                portalUsers={portalPatientOverview.LinkedAccounts}
                                showInPopup={true}
                                showHeader={PortalHelper.hasGrantedAccessToOtherProfiles(portalPatientOverview) &&
                                    !PortalHelper.hasAccessToOtherProfiles(portalPatientOverview, this.props.patientId)
                                }
                            />
                        )}

                        {isSubscriptionEnabled && portalPatientOverview != null && (
                            <SubscriptionDetails
                                subscription={portalPatientOverview.Subscription}
                                patientContactId={this.props.patientId}
                                areControlsDisabled={true}
                            />
                        )}

                        {isSubscriptionEnabled
                            && portalPatientOverview != null
                            && portalPatientOverview.Subscription != null
                            && (
                                <SubscriptionUserManagement
                                    subscription={portalPatientOverview.Subscription}
                                    patientContactId={this.props.patientId}
                                    areControlsDisabled={true}
                                />
                            )
                        }

                        {portalPatientOverview != null && (
                            <div className="block separated">
                                <div className="flex-container heading">
                                    <h3 className="h thin-font">{cacheStore.GetString("NewMessageLabel")} </h3>
                                </div>

                                {!PortalHelper.hasGrantedAccessToOtherProfiles(portalPatientOverview)
                                    && isPendingAccount
                                    && (
                                        <div className="red-text">
                                            {cacheStore.GetString("InactiveAccountWarningLabel")}
                                        </div>
                                    )
                                }

                                {PortalHelper.hasGrantedAccessToOtherProfiles(portalPatientOverview)
                                    && (isPendingAccount || isChildAccount)
                                    && (
                                        <div className="red-text">
                                            {cacheStore.GetString("ChildAccountWarningLabel")}
                                        </div>
                                    )
                                }

                                {this.state.isSendingMessage && (
                                    <InformationLoadingComponent
                                        smallVersion={true}
                                        customMessage={cacheStore.GetString("WaitForPortalLabel")}
                                    />
                                )}

                                {!this.state.isSendingMessage
                                    && !hasSendMessageToPortalError
                                    && (
                                        <FeatureFlagControl
                                            flag={this.isBidirectionalCommunicationEnabled}
                                            activeFeature={(
                                                <PortalCommunicationMessageForm
                                                    componentId="SendToPortal_NewMessageForm"
                                                    actionType={SharedItemActionType.New}
                                                    containerClass="portal-communication-message-form-container"
                                                    isPopup
                                                    hideToField
                                                    onNewPortalCommunicationMessageChange={newFormState => this.props.onSetFormState({
                                                        ...this.props.formState,
                                                        ...newFormState
                                                    })}
                                                    isMessageClickableTextPanelOpen={this.state.showMessageClickableText}
                                                    onMessageClickableTextPanelOpen={() => this.openMessageClickableTextPanel()}
                                                    onClickableTextPanelClose={() => this.closeClickableTextPanel()}
                                                />
                                            )}
                                            fallbackFeature={(
                                                this.renderSendMessageToPortal()
                                            )}
                                        />
                                    )
                                }
                                {hasSendMessageToPortalError && (
                                    <div className="red-text">
                                        {cacheStore.GetString("ErrorSendingMessageToPortalLabel")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Popup>
            </div>
        );
    }

    private renderSendMessageToPortal(): JSX.Element {
        return (
            <div className="flex-container">
                <div className="psingle-column centered">
                    <TextAreaResizable
                        id="portalMessageTextBox"
                        text={null}
                        isDisabled={false}
                        resetTextOnIdChange={false}
                        verticalResizeOnly={true}
                        minHeightPx={100}
                        style={{ maxHeight: "200px" }}
                        onChange={value => this.portalMessageChanged(value)}
                        debounceMs={0}
                    />
                    {this.state.isClickableTextOpen && (
                        <ClickableTextPanel
                            cssClass="text-helper-container portal-status-clickable"
                            context="PORTAL"
                            doNotAdjustPosition={true}
                            inputElementId="portalMessageTextBox"
                            onCloseEvent={() => this.closeClickableText()}
                            isHorizontalAligned={true}
                            horizontalAlignementElementId="portalStatusBannerPopup"
                            reduceHeight
                        />
                    )}
                    <div className="text-right">
                        <a onClick={() => { this.showClickableText(); }}>
                            {cacheStore.GetString("SelectFromListLabel")}
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    private disableSendToPortalButon(hasSendMessageToPortalError: boolean): boolean {
        const shouldDisableForEmptyContent = this.isBidirectionalCommunicationEnabled
            ? !this.props.isFormValid
            : IsNullOrWhiteSpace(this.state.portalMessageText);

        return shouldDisableForEmptyContent || hasSendMessageToPortalError || this.state.isSendingMessage;
    }

    private onCloseClick(): void {
        if (typeof this.props.onCloseCallback === "function") {
            this.props.onCloseCallback();
        }
    }

    private showClickableText(): void {
        this.mutateState(s => {
            s.isClickableTextOpen = true;
        });
    }

    private closeClickableText(): void {
        this.mutateState(s => {
            s.isClickableTextOpen = false;
        });
    }

    private portalMessageChanged(value: string): void {
        this.mutateState(s => {
            s.portalMessageText = value;
        });
    }

    /*
     * wraps logic to send a direct message to portal - firsts create a communication history, then sends it to portal
     */
    private onSendMessageClick(): void {
        const client = new Client();

        let requestData = {
            PatientId: this.props.patientInfo.InactivableResponse.Id, // inactivable Id
            Note: this.state.portalMessageText,
            EmployeeId: cacheStore.GetCurrentEmployee().ContactFacet_id,
            FollowUpLookupId: cacheStore.FindLookupByLookupCode("RFLeftMessage").LookupID,
            Version: 1
        };

        client.Post(
            apiSettings.buildApiPath(),
            ServerOperations.Server.Myle_Patient_History_CreateCommunication,
            requestData,
            response => this.onCreateCommunicationSuccess(response),
            () => this.onError()
        );

        this.mutateState(s => {
            s.isSendingMessage = true;
        });
    }

    private onCreateCommunicationSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError();
            return;
        }
        const newCommunication = ArrayHelper.Find(response.CommunicationsList, x => x.Id == response.NewCommunicationId);
        const client = new Client();

        let portalMessages: SharedItemMessage[] = [];

        let requestData = {
            ObjectId: newCommunication.Id,
            SharedItemType: PortalLinkedItemType.CommunicationHistory,
            EmployeeContactId: cacheStore.GetCurrentEmployee().Contact_id,
            PatientContactId: this.props.patientId,
            CreatedByUserId: cacheStore.GetCurrentEmployee().User_id,
            IncludeLinkedItem: false,
            Messages: portalMessages,
            SharedItemId: null,
            LabMessageDTO: null
        };

        client.Post(
            apiSettings.buildApiPath(),
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            requestData,
            response => this.onSendToPortalSuccess(response),
            () => this.onError()
        );
    }

    private onSendToPortalSuccess(response: any): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.onError();
            return;
        }

        this.mutateState(s => {
            s.isSendingMessage = false;
            s.portalMessageText = "";
        });

        this.props.onCloseCallback();
    }

    private onError(): void {
        this.mutateState(s => {
            s.isSendingMessage = false;
            s.sendToPortalErrorMessage = [cacheStore.GetString("ErrorSendingMessageToPortalLabel")];
        });
    }

    private getNewMessageValues(subjectText: string, text: string):
    { subjectText: string, messageText: string, canPatientReplyWithMessage: boolean, priority: SharedItemPriority } {
        if (!this.isBidirectionalCommunicationEnabled) {
            return {
                subjectText: subjectText,
                messageText: text,
                canPatientReplyWithMessage: false,
                priority: null
            };
        }

        const { subject, messageText, canPatientReplyWithMessage, priority } = this.props.formState;

        return {
            subjectText: subject,
            messageText: messageText,
            canPatientReplyWithMessage: canPatientReplyWithMessage,
            priority: priority
        };
    }

    private async onSendPortalCommunication(): Promise<void> {
        const { messageText, subjectText, canPatientReplyWithMessage, priority } = this.getNewMessageValues(this.state.subjectText, this.state.portalMessageText);
        const sharedItemMessage = SharedItemMessage.createNewSharedItemMessage(messageText ?? "", priority);

        const response = await Client.postAsync<void>(
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            {
                PatientContactId: this.props.patientId,
                Subject: subjectText,
                SharedItemType: PortalLinkedItemType.None,
                Messages: [sharedItemMessage],
                CanPatientReplyWithMessage: canPatientReplyWithMessage,
                IncludeLinkedItem: false
            }
        );

        if (response.errors?.any()) {
            this.onError();
            return;
        }

        this.mutateState(s => {
            s.isSendingMessage = false;
            s.portalMessageText = "";
            s.subjectText = "";
        });

        this.props.onCloseCallback?.();
    }
}

const EnhancedPortalStatusComponent = withPortalCommunicationMessageForm(PortalStatus);

export { EnhancedPortalStatusComponent as PortalStatus };
